import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    Download,
    Image,
    RotateCcw,
    Upload,
    BellRing,
    CalendarDays,
    Clock3,
    Eye,
    EyeOff,
    KeyRound,
    Lock,
    MapPin,
    Plus,
    Save,
    Settings,
    Trash2,
    WalletCards,
    Mail,
    CheckCircle,
    Server,
    Loader2,
    Calendar,
    X,
    ChevronRight,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AdminNavbar from "../../components/layout/AdminNavbar";
import SuperAdminRequestModal from "../../components/common/SuperAdminRequestModal";
import api from "../../api/axios";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useMeta } from "../../context/MetaContext";

const DEFAULT_SETTINGS = {
    leavePolicy: {
        monthlyCasualLeave: 1,
        monthlyMedicalLeave: 1,
        annualCasualLeave: 12,
        annualMedicalLeave: 12,
        compensatoryLeave: 3,
        carryForwardLeaves: true,
    },
    payCycle: {
        startDay: 25,
        endDay: 24,
        salaryBasis: '26'
    },
    workingDays: {
        days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    },
    eodPolicy: {
        deadline: "18:30",
    },
    geofence: {
        enabled: false,
        latitude: 12.9716,
        longitude: 77.5946,
        radius: 100,
    },
    salarySlip: {
        companyName: "AVIATRICKS AERO LABS PRIVATE LIMITED",
        address: "Manikampalayam, Koothampoondi, Namakkal - 637202, Tamil Nadu",
        paymentDateOffset: 0,
        authorizedSignatory: "Authorised Signatory",
        logoUrl: "",
        signatureUrl: ""
    },
    reminderEngine: {
        enabled: true,
        checkInNotDone: true,
        checkInDeadline: "10:00",
        longLunch: true,
        longLunchMinutes: 45,
        checkoutMissing: true,
        checkoutDeadline: "18:30",
        eodNotSubmitted: true,
        eodDeadline: "18:30",
        pendingLeaveApproval: true,
        policyNotAcknowledged: true,
        documentExpiring: true,
        documentExpiryDays: 30,
        salarySlipIssuePending: true,
    },
};

const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_12_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

const toTwelveHourParts = (value = "") => {
    if (!value) return { hour: "", minute: "", period: "AM" };
    const [hourValue = "", minuteValue = ""] = String(value || "").split(":");
    const hour24 = Number(hourValue);
    const minute = /^\d{2}$/.test(minuteValue) ? minuteValue : "00";
    const period = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 || 12;

    return {
        hour: Number.isNaN(hour24) ? "" : String(hour12).padStart(2, "0"),
        minute,
        period,
    };
};

const fromTwelveHourParts = ({ hour, minute, period }) => {
    const hourNumber = Number(hour);
    if (!hour || Number.isNaN(hourNumber)) return "";
    const hour24 = period === "PM" ? (hourNumber % 12) + 12 : hourNumber % 12;
    return `${String(hour24).padStart(2, "0")}:${minute || "00"}`;
};

const mergeSettings = (data = {}) => ({
    leavePolicy: { ...DEFAULT_SETTINGS.leavePolicy, ...(data.leavePolicy || {}), carryForwardLeaves: true },
    payCycle: { ...DEFAULT_SETTINGS.payCycle, ...(data.payCycle || {}) },
    workingDays: { ...DEFAULT_SETTINGS.workingDays, ...(data.workingDays || {}) },
    eodPolicy: { ...DEFAULT_SETTINGS.eodPolicy, ...(data.eodPolicy || {}) },
    geofence: { ...DEFAULT_SETTINGS.geofence, ...(data.geofence || {}) },
    reminderEngine: { ...DEFAULT_SETTINGS.reminderEngine, ...(data.reminderEngine || {}) },
});

const Field = ({ label, children }) => (
    <label className="space-y-1.5">
        <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)]">{label}</span>
        {children}
    </label>
);

const NumberInput = ({ value, onChange, min = 0, max }) => (
    <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)]"
    />
);

const ReminderTimeInput = ({ value, onChange }) => {
    const parts = toTwelveHourParts(value);
    const updatePart = (key, nextValue) => {
        onChange(fromTwelveHourParts({ ...parts, [key]: nextValue }));
    };
    const selectClass = "h-11 min-w-0 border-0 bg-transparent px-2 text-sm font-bold text-[var(--text-primary)] outline-none";

    return (
        <div className="reminder-time-input grid w-max max-w-full grid-cols-[50px_16px_50px_64px] items-center overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] focus-within:border-[var(--accent-primary)]">
            <select value={parts.hour} onChange={(event) => updatePart("hour", event.target.value)} className={selectClass} aria-label="Hour">
                <option value="">HH</option>
                {TIME_12_HOURS.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
            </select>
            <span className="grid h-11 place-items-center border-l border-[var(--border-color)] text-sm font-black text-[var(--text-secondary)]">:</span>
            <select value={parts.minute} onChange={(event) => updatePart("minute", event.target.value)} className={selectClass} aria-label="Minute">
                <option value="">MM</option>
                {TIME_MINUTES.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
            </select>
            <select value={parts.period} onChange={(event) => updatePart("period", event.target.value)} className={`${selectClass} border-l border-[var(--border-color)]`} aria-label="AM or PM">
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
        </div>
    );
};

const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const ToggleField = ({ label, checked, onChange }) => (
    <label className="flex items-center justify-between gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3">
        <span className="text-sm font-bold">{label}</span>
        <input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} />
    </label>
);

const Section = ({ icon, title, children, className = "", category, summaryIcon, summaryText, summaryValue }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="flex flex-col gap-3">
            {category && <h4 className="text-[11px] font-black uppercase tracking-wider text-[var(--accent-primary)] ml-1">{category}</h4>}
            <div onClick={() => setIsOpen(true)} className={`cursor-pointer bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-5 shadow-sm hover:shadow-md hover:border-[var(--accent-primary)] transition-all flex flex-col gap-5 ${className.replace('mt-8', '')}`}>
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-[52px] h-[52px] shrink-0 rounded-2xl bg-[var(--accent-subtle)] text-[var(--accent-primary)] flex items-center justify-center [&>svg]:w-6 [&>svg]:h-6">
                            {icon}
                        </div>
                        <div>
                            <h2 className="text-[17px] font-bold text-[var(--text-primary)]">{title}</h2>
                            <p className="text-[13px] font-medium text-[var(--text-secondary)] mt-0.5">Configure {title.toLowerCase()} settings.</p>
                        </div>
                    </div>
                    <ChevronRight className="text-[var(--text-secondary)] w-5 h-5 shrink-0" />
                </div>
                {summaryText && (
                    <div className="rounded-xl bg-[var(--bg-primary)] px-4 py-3 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2.5 font-semibold text-[var(--text-primary)]">
                            {summaryIcon && <div className="text-[var(--accent-primary)] [&>svg]:w-[18px] [&>svg]:h-[18px]">{summaryIcon}</div>}
                            {summaryText}
                        </div>
                        {summaryValue && (
                            <div className="text-[var(--text-secondary)] text-xs font-semibold">
                                {summaryValue === 'Configured' ? (
                                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold">Configured</span>
                                ) : (
                                    summaryValue
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
                        />
                        <div className="fixed inset-0 pointer-events-none z-[101] flex items-center justify-center p-4 sm:p-6">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                className="pointer-events-auto w-full max-w-3xl max-h-[90vh] flex flex-col bg-[var(--bg-primary)] rounded-3xl shadow-2xl overflow-hidden"
                            >
                                <div className="shrink-0 flex items-center justify-between p-6 border-b border-[var(--border-color)] bg-[var(--card-bg)]">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-[var(--accent-subtle)] text-[var(--accent-primary)] flex items-center justify-center [&>svg]:w-[22px] [&>svg]:h-[22px]">
                                            {icon}
                                        </div>
                                        <h2 className="text-xl font-bold text-[var(--text-primary)]">{title}</h2>
                                    </div>
                                    <button onClick={() => setIsOpen(false)} className="w-10 h-10 rounded-full flex items-center justify-center bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-subtle)] transition-all">
                                        <X size={20} />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                    {children}
                                </div>
                            </motion.div>
                        </div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

const CompanySettings = () => {
    const navigate = useNavigate();
    const { setAdmin } = useAdminAuth();
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);

    // ---- EMAIL CONFIG ----
    const [emailConfig, setEmailConfig] = useState(null);
    const [emailForm, setEmailForm] = useState({ email: "", appPassword: "", displayName: "", host: "", port: "", secure: false });
    const [showPassword, setShowPassword] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [testingEmail, setTestingEmail] = useState(false);
    const [emailLoading, setEmailLoading] = useState(true);
    const [superAdminModalOpen, setSuperAdminModalOpen] = useState(false);

    // ---- LOGO UPLOAD ----
    const { logoUrl, refreshLogo } = useMeta();
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoHistory, setLogoHistory] = useState([]);

    const handleLogoFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please select an image file (PNG, JPG, SVG, etc.)");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Logo must be less than 2MB");
            return;
        }
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const refreshLogoHistory = async () => {
        try {
            const res = await api.get("/api/settings");
            setLogoHistory(Array.isArray(res.data?.logo_history) ? res.data.logo_history : []);
        } catch (e) {
            // ignore
        }
    };

    const uploadLogo = async () => {
        if (!logoFile) return;
        // ✅ Capture logoUrl & logoHistory SYNCHRONOUSLY before any async operation
        const snapshotLogoUrl = logoUrl;
        const snapshotHistory = logoHistory;
        try {
            setLogoUploading(true);
            const reader = new FileReader();
            reader.onload = async (ev) => {
                try {
                    const newHistory = snapshotLogoUrl
                        ? [snapshotLogoUrl, ...snapshotHistory].slice(0, 10)
                        : [...snapshotHistory].slice(0, 10);

                    await api.put("/api/admin/settings", {
                        settings: {
                            logo_url: ev.target.result,
                            logo_history: newHistory,
                        }
                    });
                    // Refresh both logo and history from API to confirm save
                    await refreshLogo();
                    await refreshLogoHistory();
                    setLogoFile(null);
                    setLogoPreview(null);
                    toast.success("Logo updated successfully! All pages now show the new logo.");
                } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to upload logo");
                } finally {
                    setLogoUploading(false);
                }
            };
            reader.readAsDataURL(logoFile);
        } catch (err) {
            toast.error("Failed to read logo file");
            setLogoUploading(false);
        }
    };

    const restoreLogo = async (historyUrl) => {
        try {
            const snapshotLogoUrl = logoUrl;
            const newHistory = snapshotLogoUrl
                ? [snapshotLogoUrl, ...logoHistory.filter((h) => h !== historyUrl)].slice(0, 10)
                : logoHistory.filter((h) => h !== historyUrl);
            await api.put("/api/admin/settings", {
                settings: {
                    logo_url: historyUrl,
                    logo_history: newHistory,
                }
            });
            await refreshLogo();
            await refreshLogoHistory();
            toast.success("Previous logo restored!");
        } catch (err) {
            toast.error("Failed to restore logo");
        }
    };

    const deleteFromHistory = async (historyUrl) => {
        try {
            const newHistory = logoHistory.filter((h) => h !== historyUrl);
            await api.put("/api/admin/settings", { settings: { logo_history: newHistory } });
            setLogoHistory(newHistory);
            toast.success("Logo removed from history.");
        } catch (err) {
            toast.error("Failed to delete logo from history");
        }
    };

    const removeLogo = async () => {
        try {
            await api.put("/api/admin/settings", { 
                settings: { logo_url: "" } 
            });
            await refreshLogo();
            toast.success("Logo removed. Default logo restored.");
        } catch (err) {
            toast.error("Failed to remove logo");
        }
    };

    const [holidays, setHolidays] = useState([]);
    const [holidayForm, setHolidayForm] = useState({ name: "", date: "", isPublic: true });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [signatures, setSignatures] = useState([]);
    const [signatureForm, setSignatureForm] = useState({
        name: "",
        position: "",
        file: null,
    });
    const [signatureSaving, setSignatureSaving] = useState(false);
    const [signatureDeletingId, setSignatureDeletingId] = useState("");
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        otp: "",
    });
    const [passwordOtpSent, setPasswordOtpSent] = useState(false);
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordVisibility, setPasswordVisibility] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
        otp: false,
    });

    const updatePasswordField = (field, value) => {
        setPasswordForm((current) => ({ ...current, [field]: value }));
    };

    const togglePasswordVisibility = (field) => {
        setPasswordVisibility((current) => ({ ...current, [field]: !current[field] }));
    };

    const PasswordViewButton = ({ field, visible }) => (
        <button
            type="button"
            onClick={() => togglePasswordVisibility(field)}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[var(--text-secondary)] transition hover:bg-[var(--accent-soft)] hover:text-[var(--accent-primary)]"
            aria-label={visible ? "Hide value" : "Show value"}
            title={visible ? "Hide" : "Show"}
        >
            {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
    );

    const workingDaysLabel = useMemo(
        () => (settings.workingDays.days || []).join(", ") || "No working days selected",
        [settings.workingDays.days],
    );

    const fetchAll = async () => {
        try {
            const [settingsRes, holidaysRes, signaturesRes] = await Promise.all([
                api.get("/api/settings"),
                api.get("/api/admin/holidays"),
                api.get("/api/admin/signatures"),
            ]);
            setSettings(mergeSettings(settingsRes.data || {}));
            setLogoHistory(Array.isArray(settingsRes.data?.logo_history) ? settingsRes.data.logo_history : []);
            setHolidays(Array.isArray(holidaysRes.data) ? holidaysRes.data : []);
            setSignatures(Array.isArray(signaturesRes.data) ? signaturesRes.data : []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to load company settings");
        }
    };

    const fetchEmailConfig = async () => {
        setEmailLoading(true);
        try {
            const res = await api.get("/api/admin/settings/email-config", { params: { _t: Date.now() } });
            setEmailConfig(res.data);
            if (res.data) {
                setEmailForm(prev => ({
                    ...prev,
                    email: res.data.email || "",
                    displayName: res.data.displayName || "",
                    host: res.data.host || "",
                    port: res.data.port || "",
                    secure: res.data.secure || false,
                    appPassword: "",
                }));
            }
        } catch (err) {
            console.error("Failed to load email config", err);
        } finally {
            setEmailLoading(false);
        }
    };

    const handleSaveEmailConfig = async (e) => {
        e.preventDefault();
        setSavingEmail(true);
        try {
            const res = await api.post("/api/admin/settings/email-config", emailForm);
            toast.success(res.data.message || "Email config saved successfully!");
            await fetchEmailConfig();
            setEmailForm(prev => ({ ...prev, appPassword: "" })); // Clear password input
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save email config.");
        } finally {
            setSavingEmail(false);
        }
    };

    const handleTestEmailConfig = async () => {
        setTestingEmail(true);
        try {
            const res = await api.post("/api/admin/settings/email-config/test");
            toast.success(res.data.message || "Test email sent!");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to send test email.");
        } finally {
            setTestingEmail(false);
        }
    };

    useEffect(() => {
        document.title = "Company Settings | Savrion Technologies";
        fetchAll();
        fetchEmailConfig();
    }, []);

    const updateGroup = (group, patch) => {
        setSettings((current) => ({
            ...current,
            [group]: { ...current[group], ...patch },
        }));
    };

    const toggleWorkingDay = (day) => {
        const days = settings.workingDays.days || [];
        updateGroup("workingDays", {
            days: days.includes(day) ? days.filter((item) => item !== day) : [...days, day],
        });
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put("/api/admin/settings", {
                settings: {
                    ...settings,
                    leavePolicy: {
                        ...settings.leavePolicy,
                        monthlyCasualLeave: 1,
                        monthlyMedicalLeave: 1,
                        annualCasualLeave: 12,
                        annualMedicalLeave: 12,
                        carryForwardLeaves: true,
                    },
                },
            });
            toast.success("Company settings saved successfully");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save company settings");
        } finally {
            setSaving(false);
        }
    };

    const requestPasswordOtp = async () => {
        if (!passwordForm.currentPassword) {
            toast.error("Current password is required to request OTP");
            return;
        }
        setPasswordSaving(true);
        try {
            const res = await api.post("/api/auth/admin/password-change/request-otp", {
                currentPassword: passwordForm.currentPassword,
            });
            setPasswordOtpSent(true);
            toast.success(res.data?.message || "OTP sent to admin email");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to send password change OTP");
        } finally {
            setPasswordSaving(false);
        }
    };

    const confirmPasswordChange = async () => {
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword || !passwordForm.otp) {
            toast.error("Current password, new password, confirm password, and OTP are required");
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error("New password and confirm password do not match");
            return;
        }
        setPasswordSaving(true);
        try {
            await api.post("/api/auth/admin/password-change/confirm", {
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
                otp: passwordForm.otp,
            });
            setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "", otp: "" });
            setPasswordOtpSent(false);
            toast.success("Admin password changed successfully.");
            try {
                await api.post("/api/auth/logout");
            } catch (logoutErr) {
                console.error("Logout after password change failed:", logoutErr);
            } finally {
                localStorage.removeItem("admin_token");
                setAdmin(null);
                navigate("/admin/login", { replace: true });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update admin password");
        } finally {
            setPasswordSaving(false);
        }
    };

    const addHoliday = async (event) => {
        event.preventDefault();
        if (!holidayForm.name.trim() || !holidayForm.date) {
            toast.error("Holiday name and date are required");
            return;
        }
        try {
            const res = await api.post("/api/admin/holidays", holidayForm);
            setHolidays((current) => [...current, res.data].sort((a, b) => new Date(a.date) - new Date(b.date)));
            setHolidayForm({ name: "", date: "", isPublic: true });
            toast.success("Holiday added successfully");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to add holiday");
        }
    };

    const deleteHoliday = async (id) => {
        const ok = window.confirm("Delete this holiday?");
        if (!ok) return;
        try {
            await api.delete(`/api/admin/holidays/${id}`);
            setHolidays((current) => current.filter((holiday) => holiday._id !== id));
            toast.success("Holiday deleted");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete holiday");
        }
    };

    const addSignature = async (event) => {
        event.preventDefault();
        if (!signatureForm.name.trim() || !signatureForm.position.trim() || !signatureForm.file) {
            toast.error("Signature name, position and file are required");
            return;
        }

        try {
            setSignatureSaving(true);
            const fileData = await fileToDataUrl(signatureForm.file);
            const res = await api.post("/api/admin/signatures", {
                name: signatureForm.name.trim(),
                position: signatureForm.position.trim(),
                fileName: signatureForm.file.name,
                fileData,
            });
            setSignatures((current) =>
                [...current, res.data].sort((left, right) =>
                    String(left.name || "").localeCompare(String(right.name || ""), undefined, { sensitivity: "base" }),
                ),
            );
            setSignatureForm({ name: "", position: "", file: null });
            toast.success("Signature added successfully");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to add signature");
        } finally {
            setSignatureSaving(false);
        }
    };

    const deleteSignature = async (id) => {
        const ok = window.confirm("Delete this signature?");
        if (!ok) return;

        try {
            setSignatureDeletingId(id);
            await api.delete(`/api/admin/signatures/${id}`);
            setSignatures((current) => current.filter((signature) => signature.id !== id));
            toast.success("Signature deleted");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete signature");
        } finally {
            setSignatureDeletingId("");
        }
    };

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 min-w-0 w-full p-4 sm:p-6 lg:p-8 overflow-x-hidden">
                <div className="w-full max-w-7xl mx-auto space-y-8">
<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pb-2">
                    <div className="flex items-center gap-5 min-w-0">
                        <div className="w-16 h-16 shrink-0 rounded-[20px] bg-[var(--accent-primary)] text-white flex items-center justify-center shadow-lg shadow-purple-900/20">
                            <Settings size={28} />
                        </div>
                        <div className="min-w-0 flex flex-col justify-center">
                            <span className="text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)] mb-1">
                                Admin Configuration
                            </span>
                            <h1 className="text-3xl font-black tracking-tight text-[var(--text-primary)] leading-none">Company Settings</h1>
                            <p className="truncate text-sm font-medium text-[var(--text-secondary)] mt-2">{workingDaysLabel}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={saveSettings}
                        disabled={saving}
                        className="h-12 shrink-0 px-6 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-purple-900/20 transition-all disabled:opacity-60 cursor-pointer"
                    >
                        <Save size={18} />
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                </div>

<div className="flex flex-col gap-6 w-full max-w-4xl">
<Section icon={<Clock3 size={18} />} title="Monthly Attendance & Pay Cycle" category="ATTENDANCE & PAYROLL" summaryIcon={<CalendarDays />} summaryText="25th of Month → 24th of Next Month" summaryValue="Configured">
                    <div className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-3 items-start">
                            <Field label="Cycle Start Day of Month">
                                <select
                                    value={settings.payCycle?.startDay ?? 25}
                                    onChange={(e) => {
                                        const start = Number(e.target.value);
                                        const end = start === 1 ? 31 : (start - 1 || 24);
                                        updateGroup("payCycle", { startDay: start, endDay: end });
                                    }}
                                    className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none focus:border-[var(--accent-primary)] cursor-pointer"
                                >
                                    <option value={1}>1st of Month (1st to 30th/31st)</option>
                                    <option value={5}>5th of Month</option>
                                    <option value={10}>10th of Month</option>
                                    <option value={15}>15th of Month</option>
                                    <option value={16}>16th of Month</option>
                                    <option value={20}>20th of Month</option>
                                    <option value={21}>21st of Month</option>
                                    <option value={25}>25th of Month (Default)</option>
                                    <option value={26}>26th of Month</option>
                                </select>
                            </Field>

                            <Field label="Calculated Cycle End Day">
                                <div className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)]/60 px-3 flex items-center text-sm font-bold text-[var(--text-secondary)]">
                                    {Number(settings.payCycle?.startDay) === 1
                                        ? "Last Day of Month (30th/31st)"
                                        : `${Number(settings.payCycle?.startDay || 25) - 1}th of Next Month`}
                                </div>
                            </Field>

                            <Field label="Salary Basis">
                                <select
                                    value={settings.payCycle?.salaryBasis || '26'}
                                    onChange={(e) => updateGroup("payCycle", { salaryBasis: e.target.value })}
                                    className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none focus:border-[var(--accent-primary)] cursor-pointer"
                                >
                                    <option value="30">Fixed 30 Days</option>
                                    <option value="26">Fixed 26 Days</option>
                                    <option value="calendar">Actual Payroll Cycle Days (Dynamic)</option>
                                </select>
                            </Field>
                        </div>

                        {/* PREVIEW CARD */}
                        <div className="rounded-xl border border-[var(--accent-primary)]/20 bg-[var(--accent-subtle)] p-4 flex items-start gap-3.5 shadow-sm">
                            <div className="w-9 h-9 shrink-0 rounded-xl bg-[var(--accent-soft)] text-[var(--accent-primary)] dark:text-[var(--accent-primary)] flex items-center justify-center">
                                <CalendarDays size={18} />
                            </div>
                            <div className="min-w-0 text-xs flex-1">
                                <div className="flex items-center gap-2">
                                    <p className="font-black text-[var(--accent-primary)] dark:text-[var(--accent-primary)] uppercase tracking-wide">Active Cycle Preview</p>
                                    <span className="rounded-full bg-[var(--accent-soft)] px-2 py-0.5 text-[9px] font-black text-[var(--heading-secondary)] dark:text-[var(--heading-secondary)]">Live</span>
                                </div>
                                <p className="mt-1 text-sm font-semibold text-[var(--text-primary)]">
                                    {Number(settings.payCycle?.startDay) === 1 ? (
                                        <>Company Attendance & Payroll Cycle runs from <strong>1st</strong> of every month to the <strong>last day</strong> of that month (1st to 30th/31st).</>
                                    ) : (
                                        <>Company Attendance & Payroll Cycle runs from <strong>{settings.payCycle?.startDay || 25}th</strong> of the previous month to <strong>{Number(settings.payCycle?.startDay || 25) - 1}th</strong> of the current month.</>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </Section>

<Section icon={<Lock size={18} />} title="Admin Password & Security" category="SECURITY" summaryIcon={<ShieldCheck />} summaryText="Protected" summaryValue="Last changed: 18 Aug 2026">
                    <div className="space-y-4">
                        {/* STEP 1: CURRENT PASSWORD & OTP */}
                        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-black uppercase tracking-wider text-[var(--accent-primary)]">Step 1: Verify Current Password</span>
                                <span className="text-[11px] font-semibold text-[var(--text-secondary)]">Required to request change OTP</span>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                                <div className="relative min-w-0">
                                    <input
                                        type={passwordVisibility.currentPassword ? "text" : "password"}
                                        value={passwordForm.currentPassword}
                                        onChange={(event) => updatePasswordField("currentPassword", event.target.value)}
                                        placeholder="Enter current admin password"
                                        className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 pr-11 outline-none focus:border-[var(--accent-primary)] text-sm font-bold"
                                    />
                                    <PasswordViewButton field="currentPassword" visible={passwordVisibility.currentPassword} />
                                </div>
                                <button
                                    type="button"
                                    onClick={requestPasswordOtp}
                                    disabled={passwordSaving}
                                    className="h-11 px-5 rounded-xl border border-[var(--accent-primary)] text-[var(--accent-primary)] hover:bg-[var(--accent-primary)] hover:text-white text-sm font-bold transition-all disabled:opacity-60 cursor-pointer shrink-0"
                                >
                                    {passwordOtpSent ? "Resend OTP" : "Send Verification OTP"}
                                </button>
                            </div>
                            <p className="text-xs text-[var(--text-secondary)] font-medium">
                                OTP is sent to <strong className="text-[var(--text-primary)]">aviatrickswebsite@gmail.com</strong> and expires in 10 minutes.
                            </p>
                        </div>

                        {/* STEP 2: NEW PASSWORD FORM */}
                        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 space-y-4">
                            <span className="text-xs font-black uppercase tracking-wider text-[var(--accent-primary)] block">Step 2: Enter New Password & OTP</span>
                            <div className="grid gap-4 sm:grid-cols-3 items-end">
                                <Field label="New Password">
                                    <div className="relative">
                                        <input
                                            type={passwordVisibility.newPassword ? "text" : "password"}
                                            value={passwordForm.newPassword}
                                            onChange={(event) => updatePasswordField("newPassword", event.target.value)}
                                            className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 pr-11 outline-none focus:border-[var(--accent-primary)] text-sm font-bold"
                                        />
                                        <PasswordViewButton field="newPassword" visible={passwordVisibility.newPassword} />
                                    </div>
                                </Field>
                                <Field label="Confirm Password">
                                    <div className="relative">
                                        <input
                                            type={passwordVisibility.confirmPassword ? "text" : "password"}
                                            value={passwordForm.confirmPassword}
                                            onChange={(event) => updatePasswordField("confirmPassword", event.target.value)}
                                            className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 pr-11 outline-none focus:border-[var(--accent-primary)] text-sm font-bold"
                                        />
                                        <PasswordViewButton field="confirmPassword" visible={passwordVisibility.confirmPassword} />
                                    </div>
                                </Field>
                                <Field label="6-Digit OTP">
                                    <div className="relative">
                                        <KeyRound size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                                        <input
                                            type={passwordVisibility.otp ? "text" : "password"}
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={passwordForm.otp}
                                            onChange={(event) => updatePasswordField("otp", event.target.value.replace(/\D/g, "").slice(0, 6))}
                                            placeholder="------"
                                            className="w-full h-11 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] pl-9 pr-11 font-black tracking-[0.25em] outline-none focus:border-[var(--accent-primary)] text-sm"
                                        />
                                        <PasswordViewButton field="otp" visible={passwordVisibility.otp} />
                                    </div>
                                </Field>
                            </div>
                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={confirmPasswordChange}
                                    disabled={passwordSaving || !passwordOtpSent}
                                    className="h-11 px-8 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-sm font-bold text-white shadow-md shadow-[var(--accent-primary)]/20 transition-all disabled:opacity-50 cursor-pointer"
                                >
                                    {passwordSaving ? "Updating..." : "Confirm & Change Password"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Section>

                                
                <Section icon={<CalendarDays size={18} />} title="Holidays" category="CALENDAR & LEAVES" summaryIcon={<CalendarDays />} summaryText="Manage public & company holidays" summaryValue="Configured">
                    <form onSubmit={addHoliday} className="grid gap-3 sm:grid-cols-2">
                        <input
                            value={holidayForm.name}
                            onChange={(e) => setHolidayForm((current) => ({ ...current, name: e.target.value }))}
                            placeholder="Holiday name"
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)]"
                        />
                        <input
                            type="date"
                            value={holidayForm.date}
                            onChange={(e) => setHolidayForm((current) => ({ ...current, date: e.target.value }))}
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)]"
                        />
                        <label className="h-11 px-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] flex items-center gap-2 text-sm font-bold">
                            <input
                                type="checkbox"
                                checked={holidayForm.isPublic}
                                onChange={(e) => setHolidayForm((current) => ({ ...current, isPublic: e.target.checked }))}
                            />
                            Public
                        </label>
                        <button type="submit" className="h-11 px-4 rounded-xl bg-[var(--accent-primary)] text-white font-bold flex items-center justify-center gap-2">
                            <Plus size={16} />
                            Add
                        </button>
                    </form>

                    <div className="divide-y divide-[var(--border-color)] rounded-xl border border-[var(--border-color)] overflow-hidden">
                        {holidays.length === 0 ? (
                            <p className="p-4 text-sm text-[var(--text-secondary)]">No holidays configured.</p>
                        ) : (<>
                            <div className="hidden grid-cols-[1fr_120px_80px_44px] bg-[var(--bg-primary)] text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)] sm:grid">
                                <span className="px-3 py-2.5">Holiday</span><span className="px-3 py-2.5">Date</span><span className="px-3 py-2.5">Scope</span><span />
                            </div>
                            {holidays.map((holiday) => (
                                <div key={holiday._id} className="grid grid-cols-[1fr_auto] items-center gap-3 bg-[var(--bg-primary)] p-3 sm:grid-cols-[1fr_120px_80px_44px]">
                                    <div className="min-w-0">
                                        <p className="font-bold truncate">{holiday.name}</p>
                                        <p className="text-xs text-[var(--text-secondary)] sm:hidden">{new Date(holiday.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} • {holiday.isPublic ? "Public" : "Internal"}</p>
                                    </div>
                                    <span className="hidden text-xs font-semibold sm:block">{new Date(holiday.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                                    <span className="hidden text-xs font-bold sm:block">{holiday.isPublic ? "Public" : "Internal"}</span>
                                    <button
                                        type="button"
                                        onClick={() => deleteHoliday(holiday._id)}
                                        className="w-9 h-9 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center"
                                        title="Delete holiday"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                            </>
                        )}
                    </div>
                </Section>

                <Section icon={<Save size={18} />} title="Letter Signatures" category="DOCUMENTS" summaryIcon={<Save />} summaryText="Authorized digital signatures" summaryValue="Configured">
                    <form onSubmit={addSignature} className="grid gap-3 sm:grid-cols-2">
                        <input
                            value={signatureForm.name}
                            onChange={(e) => setSignatureForm((current) => ({ ...current, name: e.target.value }))}
                            placeholder="Signer name"
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)]"
                        />
                        <input
                            value={signatureForm.position}
                            onChange={(e) => setSignatureForm((current) => ({ ...current, position: e.target.value }))}
                            placeholder="Signer position"
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 outline-none focus:border-[var(--accent-primary)]"
                        />
                        <button
                            type="submit"
                            disabled={signatureSaving}
                            className="h-11 px-4 rounded-xl bg-[var(--accent-primary)] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60 sm:col-span-2"
                        >
                            <Plus size={16} />
                            {signatureSaving ? "Saving..." : "Add Signature"}
                        </button>
                        <label className="h-11 px-3 rounded-xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] flex items-center justify-between gap-3 text-sm font-semibold cursor-pointer sm:col-span-2">
                            <span className="truncate">{signatureForm.file?.name || "Upload signature PDF or image"}</span>
                            <input
                                type="file"
                                accept=".pdf,image/*"
                                className="hidden"
                                onChange={(e) => setSignatureForm((current) => ({ ...current, file: e.target.files?.[0] || null }))}
                            />
                            <span className="text-[var(--accent-primary)] font-bold">Choose file</span>
                        </label>
                    </form>

                    <div className="divide-y divide-[var(--border-color)] rounded-xl border border-[var(--border-color)] overflow-hidden">
                        {signatures.length === 0 ? (
                            <p className="p-4 text-sm text-[var(--text-secondary)]">No signatures added yet.</p>
                        ) : (<>
                            <div className="hidden grid-cols-[1fr_1fr_44px] bg-[var(--bg-primary)] text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)] sm:grid">
                                <span className="px-3 py-2.5">Signer</span><span className="px-3 py-2.5">Position / File</span><span />
                            </div>
                            {signatures.map((signature) => (
                                <div key={signature.id} className="grid grid-cols-[1fr_auto] items-center gap-3 bg-[var(--bg-primary)] p-3 sm:grid-cols-[1fr_1fr_44px]">
                                    <div className="min-w-0">
                                        <p className="font-bold truncate">{signature.name}</p>
                                        <p className="text-xs text-[var(--text-secondary)] truncate">
                                            {signature.position} • {signature.fileName || "Signature file"}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => deleteSignature(signature.id)}
                                        disabled={signatureDeletingId === signature.id}
                                        className="w-9 h-9 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center disabled:opacity-60"
                                        title="Delete signature"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                            </>
                        )}
                    </div>
                </Section>
                <Section icon={<Mail size={18} />} title="System Email Settings" category="COMMUNICATION" summaryIcon={<Mail />} summaryText="Automated system emails" summaryValue="Configured">
                    {emailLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="w-6 h-6 animate-spin text-[var(--accent-primary)]" />
                        </div>
                    ) : (
                        <div className="flex flex-col lg:flex-row gap-6">
                            <div className="flex-1 space-y-4">
                                <p className="text-sm text-[var(--text-secondary)]">
                                    Configure the email address used to send escalations, birthday wishes, and official letters. We recommend using a Google Workspace or Gmail account with an App Password.
                                </p>
                                
                                <form onSubmit={handleSaveEmailConfig} className="flex flex-col gap-3">
                                    <Field label="Email Address">
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Mail size={16} className="text-slate-400" />
                                            </div>
                                            <input 
                                                type="email" 
                                                required
                                                value={emailForm.email}
                                                onChange={e => setEmailForm(prev => ({ ...prev, email: e.target.value }))}
                                                className="w-full pl-9 pr-3 h-11 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]"
                                                placeholder="hr@company.com"
                                            />
                                        </div>
                                    </Field>
                                    
                                    <Field label="Display Name">
                                        <input 
                                            type="text" 
                                            value={emailForm.displayName}
                                            onChange={e => setEmailForm(prev => ({ ...prev, displayName: e.target.value }))}
                                            className="w-full px-3 h-11 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]"
                                            placeholder="Company HR"
                                        />
                                    </Field>
                                    
                                    <Field label="App Password">
                                        <div className="relative">
                                            <input 
                                                type={showPassword ? "text" : "password"}
                                                required={!emailConfig?.hasPassword}
                                                value={emailForm.appPassword}
                                                onChange={e => setEmailForm(prev => ({ ...prev, appPassword: e.target.value }))}
                                                className="w-full pl-3 pr-10 h-11 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]"
                                                placeholder={emailConfig?.hasPassword ? "•••••••••••••••• (Leave blank to keep current)" : "16-digit app password"}
                                            />
                                            <button 
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-[var(--text-primary)]"
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        <div className="flex justify-end pt-0.5">
                                            <a href="https://support.google.com/accounts/answer/185833?hl=en" target="_blank" rel="noreferrer" className="text-[10px] font-medium text-[var(--accent-primary)] hover:underline">How to get an App Password?</a>
                                        </div>
                                    </Field>

                                    <div className="pt-2 flex gap-2">
                                        <button 
                                            type="submit"
                                            disabled={savingEmail || (!emailForm.email)}
                                            className="flex-1 h-11 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white text-[13px] sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed px-2"
                                        >
                                            {savingEmail ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                            <span className="whitespace-nowrap">Save & Verify</span>
                                        </button>
                                        
                                        <button 
                                            type="button"
                                            onClick={handleTestEmailConfig}
                                            disabled={testingEmail || !emailConfig?.configured}
                                            className="px-3 sm:px-4 h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:bg-[var(--bg-primary)] text-[var(--text-primary)] text-[13px] sm:text-sm font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={!emailConfig?.configured ? "Save configuration first" : "Send test email to yourself"}
                                        >
                                            {testingEmail ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                                            Test
                                        </button>
                                    </div>
                                </form>
                            </div>
                            
                            <div className="flex-1 bg-[var(--bg-primary)] rounded-xl p-5 border border-[var(--border-color)] self-start mt-1">
                                <h4 className="text-sm font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                                    <Server size={16} className="text-[var(--accent-primary)]" />
                                    Current Status
                                </h4>
                                
                                <div className="flex flex-col gap-3.5">
                                    <div className="flex justify-between items-center pb-2.5 border-b border-[var(--border-color)]">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Status</span>
                                        {emailConfig?.configured ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active
                                            </span>
                                        ) : emailConfig?.usingEnv ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span> Using .env
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-bold">
                                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Not Configured
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="flex justify-between items-center pb-2.5 border-b border-[var(--border-color)] gap-4">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] shrink-0">Active Email</span>
                                        <span className="text-sm font-bold text-[var(--text-primary)] break-all text-right">{emailConfig?.email || "None"}</span>
                                    </div>
                                    
                                    <div className="flex justify-between items-center pb-2.5 border-b border-[var(--border-color)] gap-4">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] shrink-0">Provider</span>
                                        <span className="text-sm font-bold text-[var(--text-primary)] text-right">{emailConfig?.provider || "N/A"}</span>
                                    </div>
                                    
                                    <div className="flex justify-between items-center gap-4">
                                        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] shrink-0">App Password</span>
                                        <span className="text-sm font-bold text-[var(--text-primary)] break-all text-right">
                                            {emailConfig?.hasPassword || emailConfig?.usingEnv ? "••••••••••••••••" : "Not Set"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Section>
</div>

                {/* ========== WORKING DAYS ========== */}
                <section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-8 shadow-sm">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 rounded-2xl bg-[var(--accent-subtle)] flex items-center justify-center text-[var(--accent-primary)]">
                            <Calendar size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-[var(--text-primary)]">Default Working Days</h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-0.5">Select the global working days for the company.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                            <button
                                key={day}
                                type="button"
                                onClick={() => toggleWorkingDay(day)}
                                className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                                    (settings.workingDays?.days || []).includes(day)
                                        ? "bg-[var(--accent-primary)] text-white border-[var(--accent-primary)] shadow-md shadow-purple-900/20"
                                        : "bg-transparent border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-400"
                                }`}
                            >
                                {day}
                            </button>
                        ))}
                    </div>
                </section>

                {/* ========== COMPANY LOGO ========== */}
                <section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-8 shadow-sm">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 rounded-2xl bg-[var(--accent-subtle)] flex items-center justify-center text-[var(--accent-primary)]">
                            <Image size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-[var(--text-primary)]">Company Logo</h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-0.5">Upload your company logo. It will appear in the sidebar and across the admin panel.</p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start gap-12">
                        {/* Current Logo Preview */}
                        <div className="flex flex-col items-start gap-2 shrink-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Current Logo</p>
                            <div className="w-[280px] h-32 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] flex items-center justify-center overflow-hidden p-4">
                                {logoUrl ? (
                                    <img src={logoUrl} alt="Current Logo" className="block max-w-full max-h-full object-contain" />
                                ) : (
                                    <img src="/logo.png" alt="Default Logo" className="block max-w-full max-h-full object-contain" />
                                )}
                            </div>
                            {logoUrl && (
                                <div className="flex items-center gap-4 mt-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const a = document.createElement("a");
                                            a.href = logoUrl || "/logo.png";
                                            a.download = "company_logo.png";
                                            a.click();
                                        }}
                                        className="flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-transparent border border-[var(--border-color)] hover:border-[var(--accent-primary)] hover:bg-[var(--accent-soft)] text-[var(--text-primary)] text-sm font-semibold transition-all"
                                    >
                                        <Download size={16} />
                                        Download Logo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={removeLogo}
                                        disabled={logoUploading}
                                        className="text-xs font-bold text-red-500 hover:text-red-600 transition-colors"
                                    >
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Upload Controls */}
                        <div className="flex-1 min-w-0 w-full">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-2">Upload New Logo</p>
                            <div className="relative">
                                <input
                                    type="file"
                                    id="logoUpload"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleLogoFileChange}
                                />
                                <label
                                    htmlFor="logoUpload"
                                    className="flex flex-col items-center justify-center gap-2 w-full h-32 rounded-xl border-2 border-dashed border-[var(--heading-secondary)] bg-[var(--accent-subtle)] hover:border-[var(--accent-primary)] hover:bg-[var(--accent-soft)] cursor-pointer transition-all overflow-hidden p-3"
                                >
                                    <Upload size={22} className="text-[var(--accent-primary)] mb-1" />
                                    <span className="text-sm text-[var(--accent-primary)] font-semibold">Click to browse or drag & drop</span>
                                    <span className="text-xs text-[var(--text-secondary)]">PNG, JPG, SVG - max 2MB</span>
                                </label>
                            </div>
                            {logoPreview && (
                                <div className="mt-4 flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={uploadLogo}
                                        disabled={logoUploading}
                                        className="h-10 px-5 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                                    >
                                        {logoUploading ? "Uploading..." : <><CheckCircle size={16} /> Apply Logo</>}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setLogoPreview(null);
                                            setLogoFile(null);
                                        }}
                                        disabled={logoUploading}
                                        className="h-10 px-4 rounded-xl border border-[var(--border-color)] text-[var(--text-secondary)] text-sm font-bold hover:bg-[var(--bg-secondary)] transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ========== LOGO HISTORY ========== */}
                {logoHistory.length > 0 && (
                    <section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-8 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-2xl bg-[var(--accent-subtle)] flex items-center justify-center text-[var(--accent-primary)]">
                                <Image size={22} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-[var(--text-primary)]">Previously Used Logos</h2>
                                <p className="text-sm text-[var(--text-secondary)] mt-0.5">Click any previous logo to restore it as the current logo.</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            {logoHistory.map((url, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => restoreLogo(url)}
                                    className="relative w-24 h-16 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden hover:border-[var(--accent-primary)] hover:shadow-md transition-all group"
                                    title="Click to restore this logo"
                                >
                                    <img
                                        src={url}
                                        alt={`History ${i}`}
                                        className="w-full h-full object-contain p-2"
                                    />
                                    <div className="absolute inset-0 bg-[var(--accent-primary)]/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <RotateCcw size={16} className="text-[var(--accent-primary)]" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* ========== SPEAK WITH SUPER ADMIN & REQUESTS ========== */}
                <section className="bg-gradient-to-r from-purple-900/10 via-indigo-900/10 to-transparent border border-purple-500/20 rounded-[24px] p-8 shadow-sm relative overflow-hidden">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center border border-purple-500/20 shrink-0">
                                <Sparkles size={24} className="animate-pulse" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                                    Speak with Super Admin
                                    <span className="px-2.5 py-0.5 text-[10px] uppercase font-extrabold rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                                        Support & Quota Upgrades
                                    </span>
                                </h2>
                                <p className="text-sm text-[var(--text-secondary)] mt-0.5">
                                    Request employee limit increases, submit feedback to improve code logic and functions, or report issues directly to the Super Admin.
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setSuperAdminModalOpen(true)}
                            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-purple-500/20 transition-all flex items-center gap-2 shrink-0"
                        >
                            <Sparkles size={16} />
                            <span>Open Request Portal</span>
                        </button>
                    </div>
                </section>
                </div>
            </main>

            <SuperAdminRequestModal
                isOpen={superAdminModalOpen}
                onClose={() => setSuperAdminModalOpen(false)}
            />
        </div>
    );
};

export default CompanySettings;


